<script setup lang="ts">
import { computed } from 'vue';
import { SCHEDULES_UPDATING_TEXT, isIndexedSchedule } from '@/components/arrayColumns/scheduleCell';
import ScheduleOneLiner from './ScheduleOneLiner.vue';
import { useTableSchedules } from './useCardSchedules';

/**
 * The schedules in the table's Schedules cell, after its count badges. It draws
 * the same schedules the search card draws, each as the one-line schedule card
 * ({@link ScheduleOneLiner}). They come from {@link useTableSchedules}, so they
 * are in the card's order unless the table is sorted by a schedule column, and
 * each schedule column's cell draws its values in the same order.
 *
 * The cards sit in one row that never wraps. The row clips with an ellipsis,
 * the way the zone table's Buckets cell clips its text. Each card is an inline
 * box, so a card that does not fit is replaced by the ellipsis rather than cut
 * in half. The cell's tooltip still lists every schedule in force.
 *
 * The cell draws no checkboxes. Schedules are ticked on the search cards, and
 * the table has the list checkbox at the start of each row.
 */
const props = withDefaults(
    defineProps<{
        row: Record<string, unknown>;
        /**
         * Put each schedule's full text in a native title. The table cell turns it
         * off, because it shows its own tooltip over the row.
         */
        withTitle?: boolean;
    }>(),
    { withTitle: true },
);

const entries = useTableSchedules(() => props.row);

/** Schedules exist, but none the index has written in full yet: not "Not scheduled". */
const awaitingIndex = computed(() => {
    const items = props.row.location_zone_custom_playlist_schedules;
    return Array.isArray(items) && items.length > 0 && !items.some(isIndexedSchedule);
});
</script>

<template>
    <span v-if="entries.length > 0" class="block min-w-0 truncate" data-testid="schedule-one-liner-list">
        <span
            v-for="entry in entries"
            :key="entry.key"
            class="mr-3 inline-flex max-w-full items-center gap-1 align-middle last:mr-0"
            data-testid="schedule-one-liner-item"
            :data-schedule-id="entry.schedule.id"
        >
            <ScheduleOneLiner :entry="entry" :with-title="withTitle" />
        </span>
    </span>
    <span v-else-if="awaitingIndex" data-testid="container-schedules-text">{{ SCHEDULES_UPDATING_TEXT }}</span>
</template>
